// Pure helpers behind the Add / Edit Product form (F-FE-N137).
//
// Everything here used to live inline in ProductForm.tsx's onSubmit and two
// IIFEs in its JSX, which is why none of the audit-driven invariants below had
// a test. The form now owns state and rendering; the payload rules live here.
//
// Vocabulary: a product's cost comes from exactly ONE source in the UI —
//   'recipe'  → stock components drawn down per sale (inventory tracking);
//               the sale engine PREFERS this cost over projected_cost.
//   'amount'  → a manual estimate: projected_cost_basis='amount'
//   'percent' → a manual estimate: projected_cost_basis='percent'
//   'none'    → no cost; margin reports show the item as uncovered.
// The backend still stores both a recipe and a manual estimate side by side.
// deriveCostMode picks the one that is actually in force.

export type CostMode = 'recipe' | 'amount' | 'percent' | 'none'

export const COST_MODE_LABELS: Record<CostMode, string> = {
  recipe: 'From stock recipe',
  amount: 'Fixed amount',
  percent: '% of price',
  none: 'Not set',
}

export interface CostModeSource {
  tracked_stock_item_id?: string | null
  projected_cost?: number | null
  projected_cost_basis?: 'amount' | 'percent' | null
}

/** Whether a product carries a manual estimate (mirrors ProductForm's existingHasCost). */
export function hasManualEstimate(p: CostModeSource | undefined | null): boolean {
  if (!p) return false
  return p.projected_cost != null || p.projected_cost_basis === 'percent'
}

/** The cost source in force for an existing product; 'none' for a new one. */
export function deriveCostMode(p: CostModeSource | undefined | null): CostMode {
  if (!p) return 'none'
  if (p.tracked_stock_item_id) return 'recipe'
  if (hasManualEstimate(p)) return p.projected_cost_basis === 'percent' ? 'percent' : 'amount'
  return 'none'
}

export interface RecipeComponentPayload {
  stock_item_id: string
  /** In the STOCK item's unit — what the sale engine and reports read as-is. */
  deduct_qty: number
  deduct_unit: string | null
  /** What the operator typed, in deduct_unit (F-BE-N58 exact round-trip). */
  deduct_qty_entered: number
}

interface CostTrio {
  projected_cost_basis?: 'amount' | 'percent'
  projected_cost?: number | null
  projected_cost_percent?: number | null
}

interface InventoryFields {
  components?: RecipeComponentPayload[]
  tracked_stock_item_id?: string | null
  stock_deduct_qty?: number
}

export interface ApplyCostOptions {
  mode: CostMode
  /** The saved product already carries a manual estimate. */
  existingHasCost: boolean
  /** Recipe mode only: the operator asked to drop the dormant manual estimate. */
  removeDormantEstimate?: boolean
}

/**
 * What the form sends: the caller's own fields, with the cost trio optional
 * (it is dropped outright when costing must not change) and the inventory
 * fields always set by this function.
 */
export type ProductPayload<T> = Omit<T, keyof CostTrio | keyof InventoryFields> &
  Partial<CostTrio> &
  InventoryFields

/** basis='amount' with both values null is the server's canonical "no cost". */
const NULL_TRIO: Required<CostTrio> = {
  projected_cost_basis: 'amount',
  projected_cost: null,
  projected_cost_percent: null,
}

function dropTrio<T extends CostTrio>(data: T): Omit<T, keyof CostTrio> {
  const { projected_cost_basis: _b, projected_cost: _c, projected_cost_percent: _p, ...rest } = data
  void _b; void _c; void _p
  return rest
}

/**
 * Shape the validated form values into the product payload.
 *
 *  - The manual trio is SENT only when it is meant to change: present for
 *    amount/percent, explicit nulls when clearing an existing estimate, and
 *    otherwise dropped so the backend's "basis present = update trio" guard
 *    stays quiet.
 *  - Inventory (F-FE-N157): the product form NEVER touches the recipe. The
 *    recipe is owned by Costing & Recipes and saved through its own endpoint;
 *    `components`, `tracked_stock_item_id` and `stock_deduct_qty` are stripped
 *    here so a product edit can neither overwrite nor silently delete one
 *    (the backend leaves the recipe alone when all three are omitted).
 */
export function applyCostToPayload<T extends CostTrio>(
  data: T,
  opts: ApplyCostOptions,
): ProductPayload<T> {
  const base = dropInventory(data) as unknown as ProductPayload<T>
  switch (opts.mode) {
    case 'amount':
    case 'percent':
      return {
        ...base,
        projected_cost_basis: data.projected_cost_basis ?? opts.mode,
      }
    case 'none':
      return opts.existingHasCost
        ? { ...base, ...NULL_TRIO }
        : (dropTrio(base as unknown as T) as unknown as ProductPayload<T>)
    case 'recipe':
      return opts.existingHasCost && opts.removeDormantEstimate
        ? { ...base, ...NULL_TRIO }
        : (dropTrio(base as unknown as T) as unknown as ProductPayload<T>)
  }
}

function dropInventory<T>(data: T): Omit<T, keyof InventoryFields> {
  const { components: _c, tracked_stock_item_id: _t, stock_deduct_qty: _q, ...rest } = data as T & InventoryFields
  void _c; void _t; void _q
  return rest
}

export interface MarginFigures {
  cost: number
  profit: number
  marginPct: number
  /** cost > price with a real price set */
  loss: boolean
}

export function marginFigures(price: number, cost: number): MarginFigures {
  const p = Number(price) || 0
  const profit = p - cost
  const marginPct = p > 0 ? (profit / p) * 100 : 0
  return { cost, profit, marginPct, loss: p > 0 && cost > p }
}
